import React, { useEffect, useState } from 'react';
import {
  Button,
  Drawer,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  createProgrammaticVendor,
  deleteProgrammaticVendor,
  fetchProgrammaticVendors,
  updateProgrammaticVendor,
} from 'state/actions/programmaticVendor';

const { Text } = Typography;

const VENDOR_OPTIONS = [{ value: 'perion', label: 'Perion' }];

const ENV_OPTIONS = [
  { value: 'uat', label: 'UAT (edge-uat.hsdlv.com)' },
  { value: 'production', label: 'Production (edge.hsdlv.com)' },
];

// Credentials (orgUuid, apiKey) are not edited here — they are set per vendor
// in the screen form. This drawer only decides which vendors exist to pick from.
const emptyForm = {
  name: '',
  vendor: 'perion',
  env: 'uat',
};

export default function ProgrammaticVendorDrawer({ open, onClose, siteId }) {
  const dispatch = useDispatch();

  const { vendorList, loading } = useSelector(
    (state) => ({
      vendorList: state.programmaticVendor.list,
      loading: state.programmaticVendor.loading,
    }),
    shallowEqual
  );

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [formValues, setFormValues] = useState(emptyForm);

  useEffect(() => {
    if (open) {
      dispatch(fetchProgrammaticVendors({ siteId }));
    }
  }, [dispatch, open, siteId]);

  const openCreateForm = () => {
    setEditingVendor(null);
    setFormValues(emptyForm);
    setIsFormOpen(true);
  };

  const openEditForm = (vendor) => {
    setEditingVendor(vendor);
    setFormValues({
      name: vendor.name || '',
      vendor: (vendor.vendor || 'perion').toLowerCase(),
      env: vendor.vendorData?.env || 'uat',
    });
    setIsFormOpen(true);
  };

  const handleChange = (field, value) =>
    setFormValues((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    if (!formValues.name.trim()) {
      Modal.error({ title: 'Vendor name is required' });
      return;
    }

    const payload = {
      name: formValues.name.trim(),
      vendor: formValues.vendor,
      siteId,
      // Site-wide on purpose: a vendor is shared by every template and screen
      // on the site. Scoping it to a templateId hides it from other templates.
      templateId: null,
      // Spread the stored vendorData so editing a name never drops the orgUuid
      // set from the screen form. apiKey is omitted, so the stored key is kept.
      vendorData: {
        ...(editingVendor?.vendorData || {}),
        env: formValues.env,
      },
    };

    if (editingVendor) {
      await dispatch(
        updateProgrammaticVendor({ ...payload, id: editingVendor._id })
      );
    } else {
      await dispatch(createProgrammaticVendor(payload));
    }

    setIsFormOpen(false);
    setEditingVendor(null);
    setFormValues(emptyForm);
    dispatch(fetchProgrammaticVendors({ siteId }));
  };

  const handleDelete = async (vendor) => {
    await dispatch(deleteProgrammaticVendor({ id: vendor._id }));
    dispatch(fetchProgrammaticVendors({ siteId }));
  };

  return (
    <>
      <Drawer
        title="Programmatic Vendors"
        open={open}
        onClose={onClose}
        width={480}
        extra={
          <Button type="primary" onClick={openCreateForm}>
            Add Vendor
          </Button>
        }
      >
        {vendorList.length === 0 ? (
          <Empty description="No programmatic vendor configured for this site." />
        ) : (
          vendorList.map((vendor) => (
            <div
              key={vendor._id}
              style={{
                border: '1px solid #e8e8e8',
                borderRadius: '6px',
                padding: '12px',
                marginBottom: '12px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '8px',
                }}
              >
                <Text strong>{vendor.name}</Text>
                <Space>
                  <Button size="small" onClick={() => openEditForm(vendor)}>
                    Edit
                  </Button>
                  <Popconfirm
                    title="Delete this vendor?"
                    okText="Delete"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => handleDelete(vendor)}
                  >
                    <Button size="small" danger>
                      Delete
                    </Button>
                  </Popconfirm>
                </Space>
              </div>
              <Space size={4} wrap>
                <Tag color="blue">{vendor.vendor}</Tag>
                <Tag>{vendor.vendorData?.env || 'uat'}</Tag>
                {vendor.apiKeyPreview ? (
                  <Tag color="green">key {vendor.apiKeyPreview}</Tag>
                ) : (
                  <Tag color="red">key not set — add it in the screen form</Tag>
                )}
              </Space>
              <div style={{ marginTop: '8px' }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  org uuid: {vendor.vendorData?.orgUuid || '—'}
                </Text>
              </div>
            </div>
          ))
        )}
      </Drawer>

      <Modal
        title={editingVendor ? 'Edit Vendor' : 'Add Vendor'}
        open={isFormOpen}
        onCancel={() => setIsFormOpen(false)}
        onOk={handleSubmit}
        confirmLoading={loading}
        okText="Save"
        width={480}
      >
        <Form layout="vertical">
          <Form.Item label="Vendor Name" required>
            <Input
              placeholder="Perion — Citylink UAT"
              value={formValues.name}
              onChange={(e) => handleChange('name', e.target.value)}
            />
          </Form.Item>

          <Form.Item label="Ad Server" required>
            <Select
              value={formValues.vendor}
              options={VENDOR_OPTIONS}
              onChange={(value) => handleChange('vendor', value)}
            />
          </Form.Item>

          <Form.Item
            label="Environment"
            required
            help="Organization UUID and API key are set per vendor in the screen form."
          >
            <Select
              value={formValues.env}
              options={ENV_OPTIONS}
              onChange={(value) => handleChange('env', value)}
            />
          </Form.Item>

        </Form>
      </Modal>
    </>
  );
}
